// import {
//   DATA_TABLE,
//   NOTE_LINKED_OBJECT,
//   REGISTRATION_TABLE as REGISTRATION_TABLE_TYPE,
//   STRUCTURED_TABLE,
//   TRANSFER_TABLE as TRANSFER_TABLE_TYPE,
// } from 'constants/ModelTypeConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {RunOutputSamplesTable} from 'lab-automation/output-samples/RunOutputSamplesTableConstants';
import {DataTable} from 'notebook/data-tables/DataTableConstants';
import {LookupStructuredTable} from 'notebook/integrations/lookup/LookupStructuredTableAdapter';
import {RegistrationStructuredTable} from 'notebook/integrations/registry/RegistrationStructuredTableAdapter';
import {ResultsTable} from 'notebook/integrations/results/ResultsTableConstants';
import {BoxCreationStructuredTable} from 'notebook/integrations/samples/BoxCreationStructuredTableAdapter';
import {InventoryStructuredTable} from 'notebook/integrations/samples/InventoryStructuredTableAdapter';
import {PlateCreationStructuredTable} from 'notebook/integrations/samples/PlateCreationStructuredTableAdapter';
import {TransferStructuredTable} from 'notebook/integrations/samples/TransferStructuredTableAdapter';
import {RegistrationTable} from 'notebook/registration-tables/RegistrationTableConstants';
import {
  StructuredTableKind,
  UnknownStructuredTable,
} from 'notebook/structured-tables/StructuredTableConstants';
import {CollectibleLegacyTableType} from 'notebook/structured-tables/util/CollectibleLegacyTableType';
import {TransferTableModel} from 'notebook/transfer-tables/TransferTableConstants';
import {
  NoteLinkedObject,
  NoteLinkedObjectType,
  NotePart,
  NotePartType,
} from 'notebook/util/NoteUtilConstants';
import {ProcessTaskStructuredTable} from 'processes/tables/ProcessTaskStructuredTableAdapter';
import {NoteLinkedAssayData} from 'runs/AttachmentConstants';

/**
 * Exports a function that allows you to retrieve a structured tables from a notePart.
 *
 * Each table type has a different data structure, so we make it easy to know what you're getting
 * by returning a wrapper object with a type discriminator.
 */

export {CollectibleLegacyTableType};

export type CollectibleTableType = StructuredTableKind | CollectibleLegacyTableType;

export const allTableTypes: Array<CollectibleTableType> = [
  ...Object.values(StructuredTableKind),
  ...Object.values(CollectibleLegacyTableType),
];

interface BasicStructuredTableCollectibleData {
  isStructuredTable: true;
  tableKind: StructuredTableKind;
  legacyTableType: null;
}

interface LegacyTableCollectibleData {
  isStructuredTable: false;
  legacyTableType: CollectibleLegacyTableType;
  table: unknown;
}

export type CollectibleTableData =
  | (LegacyTableCollectibleData & {
      legacyTableType: CollectibleLegacyTableType.LEGACY_REGISTRATION_TABLE;
      table: RegistrationTable;
    })
  | (BasicStructuredTableCollectibleData & {
      tableKind: StructuredTableKind.REGISTRATION;
      table: RegistrationStructuredTable;
    })
  | (BasicStructuredTableCollectibleData & {
      tableKind: StructuredTableKind.TRANSFER;
      table: TransferStructuredTable;
    })
  | (BasicStructuredTableCollectibleData & {
      tableKind: StructuredTableKind.INVENTORY;
      table: InventoryStructuredTable;
    })
  | (LegacyTableCollectibleData & {
      legacyTableType: CollectibleLegacyTableType.LEGACY_RESULT_TABLE;
      table: DataTable;
    })
  | (BasicStructuredTableCollectibleData & {
      tableKind: StructuredTableKind.RUN_OUTPUT_SAMPLES;
      table: RunOutputSamplesTable;
    })
  | (BasicStructuredTableCollectibleData & {
      tableKind: StructuredTableKind.RESULTS;
      table: ResultsTable;
    })
  | (BasicStructuredTableCollectibleData & {
      tableKind: StructuredTableKind.PLATE_CREATION;
      table: PlateCreationStructuredTable;
    })
  | (BasicStructuredTableCollectibleData & {
      tableKind: StructuredTableKind.BOX_CREATION;
      table: BoxCreationStructuredTable;
    })
  | (LegacyTableCollectibleData & {
      legacyTableType: CollectibleLegacyTableType.LEGACY_TRANSFER_TABLE;
      table: TransferTableModel;
    })
  | (BasicStructuredTableCollectibleData & {
      tableKind: StructuredTableKind.PROCESS_TASK;
      table: ProcessTaskStructuredTable;
    })
  | (BasicStructuredTableCollectibleData & {
      tableKind: StructuredTableKind.LOOKUP;
      table: LookupStructuredTable;
    });

export default function notePartToTableDatas(
  notePart: NotePart,
  types: Array<CollectibleTableType>
): Array<CollectibleTableData> | null {
  if (notePart.type !== NotePartType.NOTE_LINKED_OBJECT || notePart.item == null) {
    return null;
  }
  const itemType = (notePart.item as NoteLinkedObject).type;

  // Legacy tables
  for (const [type, legacyTableType] of [
    [ModelTypeConstants.REGISTRATION_TABLE, CollectibleLegacyTableType.LEGACY_REGISTRATION_TABLE],
    [ModelTypeConstants.TRANSFER_TABLE, CollectibleLegacyTableType.LEGACY_TRANSFER_TABLE],
    [ModelTypeConstants.DATA_TABLE, CollectibleLegacyTableType.LEGACY_RESULT_TABLE],
  ] as Array<[string, CollectibleLegacyTableType]>) {
    if (itemType === type && types.includes(legacyTableType)) {
      return [
        {
          isStructuredTable: false,
          legacyTableType,
          table: notePart.item,
        } as CollectibleTableData,
      ];
    }
  }

  // Run output samples table and results table
  if (
    itemType === NoteLinkedObjectType.NOTE_LINKED_ASSAY_DATA &&
    (notePart.item as NoteLinkedAssayData).versionedData != null
  ) {
    const tableDatas: Array<CollectibleTableData> = [];
    for (const structuredTable of (notePart.item as NoteLinkedAssayData).versionedData.structuredTables) {
      if (types.includes(structuredTable.kind)) {
        tableDatas.push({
          isStructuredTable: true,
          tableKind: structuredTable.kind,
          legacyTableType: null,
          table: structuredTable,
        } as CollectibleTableData);
      }
    }
    return tableDatas.length > 0 ? tableDatas : null;
  }

  // Structured table
  if (
    itemType === NoteLinkedObjectType.STRUCTURED_TABLE &&
    types.includes((notePart.item as UnknownStructuredTable).kind)
  ) {
    return [
      {
        isStructuredTable: true,
        tableKind: (notePart.item as UnknownStructuredTable).kind,
        legacyTableType: null,
        table: notePart.item,
      } as CollectibleTableData,
    ];
  }

  return null;
}
